import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext, FieldArray } from "formik";
import debounce from "lodash/debounce";
import axios from "axios";
import {
  CheckCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Switch, Button, Select } from "antd";
import trans from "../../../utils";
import {
  Input,
  TextAreaInput,
  DatePickerInput,
} from "../../Helpers/Input/Input";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import {
  useLazyLookupCustomerByPhoneQuery,
  useGetModelsByProductQuery,
  useGetIssuesByModelQuery,
  useGetSparePartsByIssuesQuery,
  useGetVillagesByMarkazQuery,
  useLazyGetServiceHistoryQuery,
} from "../../../Redux/service/CallCenter/CallCenter";
import { useGetAllSkusQuery } from "../../../Redux/service/Sku/Sku";
import { EgyptGovernorates } from "../../../constants";
import { getMarkazOptions, getGovernorateCenter } from "../../../utils/CountryState/egyptMarkaz";
import CallCenterMapPicker from "./CallCenterMapPicker";
import ContactNumberList from "../ContactNumberList/ContactNumberList";
import { COMPLAINT_DESCRIPTION_MAX_LENGTH, MAX_SECONDARY_PHONES } from "./callCenterValidation";
import CreateIssueModal from "./CreateIssueModal";
import CreateSparePartModal from "./CreateSparePartModal";
import CreateVillageModal from "./CreateVillageModal";
import PreviousServiceHistory from "./PreviousServiceHistory";
import {
  SERVICE_REQUEST_COLORS,
  getLocalizedColorLabel,
} from "./serviceRequestColors";

const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";
const NOMINATIM_SEARCH_URL = "https://nominatim.openstreetmap.org/search";

// Governorate → Markaz → Village cascade zoom levels; a manual map click
// keeps whichever zoom the cascade last landed on.
const ZOOM_GOVERNORATE = 9;
const ZOOM_MARKAZ = 12;
const ZOOM_VILLAGE = 15;

// Village/neighborhood has no dataset in this codebase (unlike Governorate
// and Markaz, which read the same cities.js POI uses) — forward geocode is
// the only option at that granularity.
const forwardGeocode = async (query, limit = 1) => {
  try {
    const { data } = await axios.get(NOMINATIM_SEARCH_URL, {
      params: { q: query, format: "jsonv2", limit, countrycodes: "eg" },
    });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    return [];
  }
};

export default function CallCenterFormBody() {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();

  const [generalInfoEditable, setGeneralInfoEditable] = useState(false);
  const [addressEditable, setAddressEditable] = useState(false);
  const [lookupMessage, setLookupMessage] = useState(null); // { type: 'success'|'info'|'error', text }
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showSparePartModal, setShowSparePartModal] = useState(false);
  const [showVillageModal, setShowVillageModal] = useState(false);
  const [customerPoiId, setCustomerPoiId] = useState(null);
  // The currently selected village's display name, sourced from wherever it
  // was last resolved (customer lookup, edit fetch, or the "Add New Village"
  // modal) — lets the Select show a correct label immediately, even before
  // (or if) the curated per-Markaz list has it, instead of rendering blank.
  const [externalVillage, setExternalVillage] = useState(null); // { id, name } | null — name already localized by the API

  const [triggerLookup, { isFetching: isLookingUp }] =
    useLazyLookupCustomerByPhoneQuery();
  const [triggerServiceHistory, { data: historyData, isFetching: historyLoading, error: historyError }] =
    useLazyGetServiceHistoryQuery();

  // Undefined until a cascade step (or manual click) resolves a location —
  // CallCenterMapPicker falls back to its own default zoom until then.
  const [mapZoom, setMapZoom] = useState();

  const lang = localStorage.getItem("lang") === "ar" ? "ar" : "en";

  const colorOptions = SERVICE_REQUEST_COLORS.map((c) => {
    const label = getLocalizedColorLabel(c.value, lang);
    return {
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              display: "inline-block",
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: c.color,
              border: "1px solid #d9d9d9",
            }}
          />
          {label}
        </div>
      ),
      value: label,
    };
  });

  const customerFound = values.customerFound; // null = not searched yet, true/false = search result

  const debouncedLookup = useMemo(
    () =>
      debounce(async (phone) => {
        try {
          const result = await triggerLookup(phone).unwrap();
          if (result?.data) {
            // The agent may have searched using a secondary/additional number —
            // always show the customer's actual primary number here, not
            // whatever was typed, so it doesn't look like the additional
            // number became the primary one.
            setFieldValue(
              "mobileNumber",
              result.data.phoneNumber || result.data.landline || phone,
            );
            setFieldValue("customerName", result.data.name || "");
            setFieldValue("state", result.data.state || "");
            setFieldValue("city", result.data.city || "");
            setFieldValue("villageId", result.data.villageId || "");
            setExternalVillage(
              result.data.villageId
                ? { id: result.data.villageId, name: result.data.villageName }
                : null,
            );
            setFieldValue("address", result.data.address || "");
            setFieldValue("latitude", result.data.latitude ?? "");
            setFieldValue("longitude", result.data.longitude ?? "");
            setFieldValue("landline", result.data.landline || "");
            setFieldValue(
              "secondaryPhones",
              Array.isArray(result.data.secondaryPhones) ? result.data.secondaryPhones : [],
            );
            // The normalized number that was actually searched — may match
            // the primary, the landline, or one of several secondary phones;
            // ContactNumberList highlights whichever one it equals.
            setFieldValue("matchedPhoneNumber", result.data.matchedPhoneNumber || null);
            setFieldValue("customerFound", true);
            setGeneralInfoEditable(false);
            setAddressEditable(false);
            setLookupMessage({
              type: "success",
              text: trans("Existing Customer Found"),
            });
            setCustomerPoiId(result.data.id);
            triggerServiceHistory(result.data.id);
          } else {
            setFieldValue("customerFound", false);
            setFieldValue("customerName", "");
            setFieldValue("state", "");
            setFieldValue("city", "");
            setFieldValue("villageId", "");
            setExternalVillage(null);
            setFieldValue("address", "");
            setFieldValue("latitude", "");
            setFieldValue("longitude", "");
            setFieldValue("landline", "");
            setFieldValue("secondaryPhones", []);
            setFieldValue("matchedPhoneNumber", null);
            setLookupMessage({
              type: "info",
              text: trans(
                "No customer found. Please enter customer information.",
              ),
            });
            setCustomerPoiId(null);
          }
        } catch (err) {
          // Lookup service failed — don't leave the agent stuck. Fall through to
          // the same "new customer" state as an explicit not-found result so they
          // can enter the customer manually; creating the request will persist a
          // new customer record, so this phone number is retrievable next time.
          setFieldValue("customerFound", false);
          setFieldValue("customerName", "");
          setFieldValue("state", "");
          setFieldValue("city", "");
          setFieldValue("villageId", "");
          setExternalVillage(null);
          setFieldValue("address", "");
          setFieldValue("latitude", "");
          setFieldValue("longitude", "");
          setFieldValue("landline", "");
          setFieldValue("secondaryPhones", []);
          setFieldValue("matchedPhoneNumber", null);
          setLookupMessage({
            type: "error",
            text: trans(
              "No customer found. Please enter customer information.",
            ),
          });
          setCustomerPoiId(null);
        }
      }, 500),
    [triggerLookup, setFieldValue],
  );

  useEffect(() => () => debouncedLookup.cancel(), [debouncedLookup]);

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    setFieldValue("mobileNumber", raw);
    setLookupMessage(null);
    // Clear any explicit selection from a previous search immediately, so it
    // can't be mistaken for a match against whatever customer this new
    // number resolves to. ContactNumberList falls back to the primary number
    // by default whenever there's no explicit selection (see below).
    setFieldValue("matchedPhoneNumber", null);
    const digits = raw.replace(/\D/g, "");
    if (digits.length >= 9) {
      debouncedLookup(raw);
    } else {
      setFieldValue("customerFound", null);
    }
  };

  const { data: skusData } = useGetAllSkusQuery();
  const productOptions = (skusData?.data || []).map((s) => ({
    label: s.skuName,
    value: String(s.id),
  }));

  const { data: modelsData } = useGetModelsByProductQuery(values.productId, {
    skip: !values.productId,
  });
  const modelOptions = modelsData?.options || [];

  const { data: issuesData, refetch: refetchIssues } = useGetIssuesByModelQuery({ modelId: values.modelId, lang }, {
    skip: !values.modelId,
  });
  const issueOptions = (issuesData?.data || []).map((issue) => ({
    label: issue.name,
    value: issue.id,
  }));

  const { data: sparePartsData, refetch: refetchSpareParts } = useGetSparePartsByIssuesQuery({ issueIds: values.defects, lang }, {
    skip: !values.defects?.length,
  });
  const sparePartOptions = (sparePartsData?.data || []).map((part) => ({
    label: `${part.partCode} - ${part.name}`,
    value: part.id,
  }));

  const handleProductChange = (val) => {
    setFieldValue("productId", val);
    setFieldValue("modelId", "");
    setFieldValue("defects", []);
    setFieldValue("sparePartIds", []);
  };

  const handleModelChange = (val) => {
    setFieldValue("modelId", val);
    setFieldValue("defects", []);
    setFieldValue("sparePartIds", []);
  };

  // ** Governorate select — zooms/pins to the governorate center (derived
  //    from the same cities.js dataset POI uses) and resets the dependent
  //    Markaz/Village cascade, same as POI resetting city on state change.
  const handleGovernorateChange = (val) => {
    setFieldValue("state", val);
    setFieldValue("city", "");
    setFieldValue("villageId", "");
    setExternalVillage(null);

    const center = getGovernorateCenter(val);
    if (center) {
      setFieldValue("latitude", center.lat);
      setFieldValue("longitude", center.lon);
      setFieldValue("address", `${val}, Egypt`);
      setMapZoom(ZOOM_GOVERNORATE);
    }
  };

  // ** Markaz options — same cities.js dataset POI's own City dropdown reads,
  //    filtered to the selected Governorate. Real coordinates, no API call.
  const markazOptions = useMemo(
    () => getMarkazOptions(values.state),
    [values.state],
  );

  const handleMarkazChange = (val) => {
    setFieldValue("city", val || "");
    setFieldValue("villageId", "");
    setExternalVillage(null);
    const selected = markazOptions.find((o) => o.value === val);
    if (selected) {
      setFieldValue("latitude", selected.lat);
      setFieldValue("longitude", selected.lon);
      setFieldValue("address", `${val}, ${values.state}, Egypt`);
      setMapZoom(ZOOM_MARKAZ);
    }
  };

  // ** Village options — a curated, per-(Governorate, Markaz) picklist backed
  //    by the new Village lookup table, plus an "Add New Village" modal for
  //    entries not yet in the system (see CreateVillageModal.jsx). The API
  //    resolves the display name server-side from the request's language
  //    (Accept-Language header, see Redux/service/CallCenter/CallCenter.js),
  //    so `name` here is already the correct text for the current UI language.
  const { data: villagesData, refetch: refetchVillages } = useGetVillagesByMarkazQuery(
    { governorate: values.state, markaz: values.city, lang },
    { skip: !values.state || !values.city },
  );
  const fetchedVillageOptions = (villagesData?.data || []).map((v) => ({
    label: v.name,
    value: v.id,
  }));
  // Preserve a village that isn't (yet, or no longer) in the curated
  // per-Markaz list — e.g. a customer record whose villageId belongs to a
  // Markaz that's since been corrected, or the split-second before the
  // curated list has loaded — using the display name that came with it
  // (customer lookup / edit fetch / "Add New Village") instead of letting
  // antd's Select silently blank out an unmatched value.
  const villageOptions = useMemo(() => {
    if (
      values.villageId &&
      !fetchedVillageOptions.some((o) => String(o.value) === String(values.villageId))
    ) {
      const fallbackLabel =
        externalVillage && String(externalVillage.id) === String(values.villageId)
          ? externalVillage.name
          : String(values.villageId);
      return [...fetchedVillageOptions, { label: fallbackLabel, value: values.villageId }];
    }
    return fetchedVillageOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villagesData, values.villageId, externalVillage]);

  // ** Village pin — resolved once per selection (not per keystroke, since
  //    picking from a dropdown is a discrete action); a manual map click still
  //    always wins as the final, most precise override. Nominatim geocodes by
  //    name, not id, so this always takes the resolved English name.
  const resolveVillagePin = async (villageName, city, governorate) => {
    if (!villageName || !governorate) return;
    const [first] = await forwardGeocode(
      `${villageName}, ${city || ""}, ${governorate}, Egypt`,
      1,
    );
    if (first) {
      setFieldValue("latitude", Number(first.lat));
      setFieldValue("longitude", Number(first.lon));
      setFieldValue("address", first.display_name || "");
      setMapZoom(ZOOM_VILLAGE);
    }
  };

  const handleVillageChange = (val) => {
    setFieldValue("villageId", val || "");
    const selected = (villagesData?.data || []).find((v) => String(v.id) === String(val));
    if (selected) {
      setExternalVillage({ id: selected.id, name: selected.name });
      resolveVillagePin(selected.name, values.city, values.state);
    }
  };

  // Disabled until we know it's a brand-new customer; once an existing customer is
  // found, stays locked unless the section's own Edit toggle is switched on (AC4).
  const generalDisabled =
    customerFound === true ? !generalInfoEditable : customerFound !== false;
  const addressDisabled =
    customerFound === true ? !addressEditable : customerFound !== false;
  // Customer Name/Code never becomes editable for an existing customer (AC4) —
  // only unlocks when we're certain this is a new customer.
  const nameDisabled = customerFound !== false;
  // Cascade: Markaz needs a Governorate, Village needs a Markaz.
  const markazDisabled = addressDisabled || !values.state;
  const villageDisabled = addressDisabled || !values.city;

  return (
    <div className="form-section">
      {lookupMessage && (
        <div
          className={`alert alert-${
            lookupMessage.type === "error"
              ? "danger"
              : lookupMessage.type === "success"
                ? "success"
                : "warning"
          } mb-3 d-flex align-items-center`}
        >
          {lookupMessage.type === "success" ? (
            <CheckCircleOutlined className="me-2" />
          ) : (
            <ExclamationCircleOutlined className="me-2" />
          )}
          <strong>{lookupMessage.text}</strong>
        </div>
      )}

      {customerFound === true && customerPoiId && (
        <PreviousServiceHistory
          historyData={historyData}
          historyLoading={historyLoading}
          historyError={historyError}
          lang={lang}
        />
      )}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="fs-16 fw-500 brand-700 text-capitalize mb-0">
          {trans("General Information")}
        </p>
        {customerFound === true && (
          <span
            role="button"
            className="d-flex align-items-center gap-1 text-primary"
            onClick={() => setGeneralInfoEditable((v) => !v)}
          >
            <EditOutlined /> {trans("Edit")}
          </span>
        )}
      </div>
      <div className="row">
        <div className="col-lg-6">
          <Input
            title={trans("Phone Number")}
            type="text"
            placeholder={trans("Enter mobile or landline number")}
            id="mobileNumber"
            name="mobileNumber"
            formstyle="phone-input"
            value={values.mobileNumber}
            onChange={handlePhoneChange}
            onBlur={handleBlur}
            errors={errors.mobileNumber}
            touched={touched.mobileNumber}
            suffix={isLookingUp ? <LoadingOutlined spin /> : null}
          />
        </div>
        <div className="col-lg-6">
          <Input
            title={trans("Customer Name")}
            type="text"
            placeholder={trans("Customer Name")}
            id="customerName"
            name="customerName"
            formstyle="form-input"
            value={values.customerName}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.customerName}
            touched={touched.customerName}
            disabled={nameDisabled}
          />
        </div>
        <div className="col-lg-12 mt-2">
          <p className="fs-14 fw-500 brand-700 mb-2">{trans("Contact Number")}</p>
          <FieldArray name="secondaryPhones">
            {({ push, remove }) => {
              const rows = [
                { key: "landline", kind: "landline", value: values.landline || "" },
                ...(values.secondaryPhones || []).map((phone, index) => ({
                  key: `secondary-${index}`,
                  kind: "secondary",
                  index,
                  value: phone,
                })),
              ];
              const errorsByKey = {};
              const touchedByKey = {};
              (values.secondaryPhones || []).forEach((_, index) => {
                const key = `secondary-${index}`;
                errorsByKey[key] = Array.isArray(errors.secondaryPhones)
                  ? errors.secondaryPhones[index]
                  : undefined;
                touchedByKey[key] = Array.isArray(touched.secondaryPhones)
                  ? touched.secondaryPhones[index]
                  : touched.secondaryPhones;
              });

              const handleChangeRow = (key, value) => {
                if (key === "landline") {
                  setFieldValue("landline", value);
                } else {
                  setFieldValue(`secondaryPhones.${Number(key.split("-")[1])}`, value);
                }
              };
              const handleRemoveRow = (key) => {
                if (key === "landline") {
                  setFieldValue("landline", "");
                } else {
                  remove(Number(key.split("-")[1]));
                }
              };

              return (
                <ContactNumberList
                  primaryNumber={values.mobileNumber || ""}
                  // Falls back to the primary number whenever the agent
                  // hasn't made an explicit selection (auto-match not yet
                  // resolved, or a brand-new customer) — selecting is always
                  // available regardless, so this is just the sane default.
                  matchedNumber={values.matchedPhoneNumber || values.mobileNumber || null}
                  rows={rows}
                  editable={!generalDisabled}
                  selectable={!!values.mobileNumber}
                  onSelect={(value) => setFieldValue("matchedPhoneNumber", value)}
                  onChangeRow={handleChangeRow}
                  onRemoveRow={handleRemoveRow}
                  onAddRow={() => push("")}
                  canAddMore={(values.secondaryPhones || []).length < MAX_SECONDARY_PHONES}
                  errorsByKey={errorsByKey}
                  touchedByKey={touchedByKey}
                />
              );
            }}
          </FieldArray>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
        <p className="fs-16 fw-500 brand-700 text-capitalize mb-0">
          {trans("Address Information")}
        </p>
        {customerFound === true && (
          <span
            role="button"
            className="d-flex align-items-center gap-1 text-primary"
            onClick={() => setAddressEditable((v) => !v)}
          >
            <EditOutlined /> {trans("Edit")}
          </span>
        )}
      </div>
      <div className="row">
        <div className="col-lg-6">
          <InputSelect
            id="state"
            name="state"
            label={trans("Governorate")}
            placeholder={trans("Select Governorate")}
            options={EgyptGovernorates}
            value={values.state || undefined}
            onChange={handleGovernorateChange}
            onBlur={() => setFieldTouched("state", true)}
            errors={errors.state}
            touched={touched.state}
            disabled={addressDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="city"
            name="city"
            label={trans("Markaz")}
            placeholder={trans(
              values.state ? "Select Markaz" : "Select Governorate first",
            )}
            options={markazOptions}
            value={values.city || undefined}
            onChange={handleMarkazChange}
            onBlur={() => setFieldTouched("city", true)}
            errors={errors.city}
            touched={touched.city}
            disabled={markazDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0 text-capitalize">
              {trans("Village / Neighborhood")}
            </label>
            {values.city && !villageDisabled && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setShowVillageModal(true)}
              >
                {lang === "ar" ? "إضافة قرية جديدة" : "Add New Village"}
              </Button>
            )}
          </div>
          <InputSelect
            id="villageId"
            name="villageId"
            placeholder={trans(values.city ? "Select Village" : "Select Markaz first")}
            options={villageOptions}
            value={values.villageId || undefined}
            onChange={handleVillageChange}
            onBlur={() => setFieldTouched("villageId", true)}
            errors={errors.villageId}
            touched={touched.villageId}
            disabled={villageDisabled}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-12">
          <TextAreaInput
            title={trans("Detailed Address")}
            placeholder={trans("Detailed Address")}
            formstyle="form-input"
            id="address"
            name="address"
            value={values.address}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.address}
            touched={touched.address}
            disabled={addressDisabled}
            rows={2}
          />
        </div>
      </div>

      {customerFound !== null && (
        <CallCenterMapPicker
          latitude={values.latitude}
          longitude={values.longitude}
          zoom={mapZoom}
          address={values.address}
          disabled={addressDisabled}
          onPick={({ latitude, longitude, address }) => {
            setFieldValue("latitude", latitude);
            setFieldValue("longitude", longitude);
            // Address always reflects the exact pinned location, same as POI's map.
            setFieldValue("address", address || "");
          }}
        />
      )}

      <p className="fs-16 fw-500 brand-700 text-capitalize mb-2 mt-3">
        {trans("Product Information")}
      </p>
      <div className="row">
        <div className="col-lg-6">
          <InputSelect
            id="productId"
            name="productId"
            label={trans("Product")}
            placeholder={trans("Select Product")}
            options={productOptions}
            value={values.productId || undefined}
            onChange={handleProductChange}
            onBlur={() => setFieldTouched("productId", true)}
            errors={errors.productId}
            touched={touched.productId}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="modelId"
            name="modelId"
            label={trans("Model")}
            placeholder={trans("Select Model")}
            options={modelOptions}
            value={values.modelId || undefined}
            onChange={handleModelChange}
            disabled={!values.productId}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-6">
          <label className="form-label">{lang === "ar" ? "لون المنتج" : "Model Color"}</label>
          <Select
            id="color"
            name="color"
            placeholder={trans("Select Color")}
            options={colorOptions}
            value={values.color || undefined}
            onChange={(val) => setFieldValue("color", val)}
            style={{ width: "100%" }}
          />
        </div>
        <div className="col-lg-12">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">{lang === "ar" ? "عيوب / مشاكل" : "Defects / Issues"}</label>
            {values.modelId && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setShowIssueModal(true)}
              >
                {lang === "ar" ? "إضافة عيب جديد" : "Add New Issue"}
              </Button>
            )}
          </div>
          <InputSelect
            id="defects"
            name="defects"
            placeholder={lang === "ar" ? (values.modelId ? "اختر العيوب" : "اختر الموديل أولاً") : (values.modelId ? "Select Defects" : "Select Model first")}
            mode="multiple"
            options={issueOptions}
            value={values.defects || undefined}
            onChange={(val) => {
              setFieldValue("defects", val || []);
              setFieldValue("sparePartIds", []);
            }}
            disabled={!values.modelId}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-12">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">{lang === "ar" ? "قطع غيار" : "Spare Parts"}</label>
            {values.defects?.length === 1 && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setShowSparePartModal(true)}
              >
                {lang === "ar" ? "إضافة قطعة غيار جديدة" : "Add New Spare Part"}
              </Button>
            )}
          </div>
          <InputSelect
            id="sparePartIds"
            name="sparePartIds"
            placeholder={lang === "ar" ? (values.defects?.length ? "اختر قطع الغيار" : "اختر العيوب أولاً") : (values.defects?.length ? "Select Spare Parts" : "Select Defects first")}
            mode="multiple"
            options={sparePartOptions}
            value={values.sparePartIds || undefined}
            onChange={(val) => setFieldValue("sparePartIds", val || [])}
            disabled={!values.defects?.length}
            formstyle="form-input"
          />
        </div>
        <div className="col-lg-12">
          <DatePickerInput
            title={`${trans("Preferred Visit Time")} (${trans("optional")})`}
            formstyle="form-input"
            disablePastDates
            errors={errors.preferredVisitTime}
            touched={touched.preferredVisitTime}
            onChange={({ startDate, endDate }) => {
              setFieldValue(
                "preferredVisitTime",
                startDate ? startDate.format(DATE_FORMAT) : "",
              );
              setFieldValue(
                "preferredVisitTimeEnd",
                endDate ? endDate.format(DATE_FORMAT) : "",
              );
              setFieldTouched("preferredVisitTime", true);
            }}
            value={
              values.preferredVisitTime && values.preferredVisitTimeEnd
                ? [values.preferredVisitTime, values.preferredVisitTimeEnd]
                : null
            }
          />
        </div>
        <div className="col-lg-12 d-flex align-items-center gap-2 mt-2 mb-2">
          <Switch
            id="callBeforePerforming"
            name="callBeforePerforming"
            checked={!!values.callBeforePerforming}
            onChange={(checked) => setFieldValue("callBeforePerforming", checked)}
          />
          <label htmlFor="callBeforePerforming" className="form-label mb-0">
            {lang === "ar" ? "اتصل قبل التحرك" : "Call Before Performing"}
          </label>
        </div>
        <div className="col-lg-12">
          <TextAreaInput
            title={trans("Complaint Description")}
            placeholder={trans("Write complaint description")}
            formstyle="form-input"
            id="complaintDescription"
            name="complaintDescription"
            value={values.complaintDescription}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.complaintDescription}
            touched={touched.complaintDescription}
            maxLength={COMPLAINT_DESCRIPTION_MAX_LENGTH}
          />
        </div>
      </div>

      <CreateIssueModal
        visible={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        modelId={values.modelId}
        onSuccess={async (newIssue) => {
          await refetchIssues();
          setFieldValue("defects", [...(values.defects || []), newIssue.id]);
        }}
      />

      <CreateSparePartModal
        visible={showSparePartModal}
        onClose={() => setShowSparePartModal(false)}
        issueId={values.defects?.[0]}
        onSuccess={async (newSparePart) => {
          await refetchSpareParts();
          setFieldValue("sparePartIds", [...(values.sparePartIds || []), newSparePart.id]);
        }}
      />

      <CreateVillageModal
        visible={showVillageModal}
        onClose={() => setShowVillageModal(false)}
        governorate={values.state}
        markaz={values.city}
        onSuccess={async (newVillage) => {
          await refetchVillages();
          setFieldValue("villageId", newVillage.id);
          setExternalVillage({ id: newVillage.id, name: newVillage.name });
          resolveVillagePin(newVillage.name, values.city, values.state);
        }}
      />
    </div>
  );
}
